import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { GosiVm, ApiResponse } from '@/api/types'

/**
 * GOSI Setting service
 * Handles GOSI configuration API operations
 */
export class GosiService {
  /**
   * Get GOSI settings for a branch
   */
  async getGosi(branchId: number): Promise<GosiVm | null> {
    try {
      const url = endpoints.gosiSetting.get(branchId)
      const response = await apiClient.get<ApiResponse<GosiVm> | GosiVm>(url)
      
      // Handle both wrapped and direct responses
      if ('data' in response.data && response.data.data) {
        return (response.data as ApiResponse<GosiVm>).data
      }
      
      // Check if response is empty/null (no settings saved yet)
      const data = response.data as GosiVm
      if (!data || (data.id === 0 && !data.socialSecurityPercent && !data.companyPercent)) {
        return null
      }
      
      return data
    } catch (error: any) {
      // If 404 or empty response, return null (no settings exist)
      if (error.response?.status === 404 || error.response?.status === 204) {
        return null
      }
      throw error
    }
  }

  /**
   * Add or update GOSI settings
   * Matches Swagger working request: application/json-patch+json content type
   */
  async addOrUpdateGosi(data: GosiVm): Promise<ApiResponse<GosiVm>> {
    const url = endpoints.gosiSetting.addOrUpdate
    console.log('🔵 [GosiService.addOrUpdateGosi] API URL:', url)
    console.log('🔵 [GosiService.addOrUpdateGosi] Payload being sent:', JSON.stringify(data, null, 2))
    console.log('🔵 [GosiService.addOrUpdateGosi] Payload type:', typeof data)
    console.log('🔵 [GosiService.addOrUpdateGosi] Payload keys:', Object.keys(data))
    
    try {
      // Use application/json-patch+json as Swagger does (or application/json - both work)
      const response = await apiClient.post<any>(
        url,
        data,
        {
          headers: {
            'Content-Type': 'application/json-patch+json',
            'Accept': 'application/json',
          },
        }
      )
      
      console.log('✅ [GosiService.addOrUpdateGosi] Full response:', response)
      console.log('✅ [GosiService.addOrUpdateGosi] Response data:', response.data)
      console.log('✅ [GosiService.addOrUpdateGosi] Response status:', response.status)
      
      // Handle Swagger response format: {"isError": false, "msg": "", "id": 0}
      const responseData = response.data
      console.log('🔵 [GosiService.addOrUpdateGosi] Raw response data:', responseData)
      console.log('🔵 [GosiService.addOrUpdateGosi] Response data type:', typeof responseData)
      console.log('🔵 [GosiService.addOrUpdateGosi] Response data keys:', responseData ? Object.keys(responseData) : 'null')
      
      if (responseData && typeof responseData === 'object') {
        // If response has isError field (Swagger format: {"isError": false, "msg": "", "id": 0})
        if ('isError' in responseData) {
          console.log('🔵 [GosiService.addOrUpdateGosi] Detected Swagger response format with isError')
          if (responseData.isError === false || responseData.isError === 0) {
            // Success - update data with returned id if available
            const updatedData: GosiVm = {
              ...data,
              id: responseData.id !== undefined ? Number(responseData.id) : data.id,
            }
            console.log('✅ [GosiService.addOrUpdateGosi] Success - returning updated data with id:', updatedData.id)
            return {
              success: true,
              message: responseData.msg || 'GOSI settings saved successfully',
              data: updatedData,
            } as ApiResponse<GosiVm>
          } else {
            // Error from API
            const errorMsg = responseData.msg || 'Failed to save GOSI settings'
            console.error('❌ [GosiService.addOrUpdateGosi] API returned isError=true:', errorMsg)
            throw new Error(errorMsg)
          }
        }
        // If response is ApiResponse format
        if ('success' in responseData) {
          console.log('🔵 [GosiService.addOrUpdateGosi] Detected ApiResponse format')
          return responseData as ApiResponse<GosiVm>
        }
        // If response is direct GosiVm
        console.log('🔵 [GosiService.addOrUpdateGosi] Detected direct GosiVm format')
        return {
          success: true,
          message: 'GOSI settings saved successfully',
          data: responseData as GosiVm,
        } as ApiResponse<GosiVm>
      }
      
      // Default success response (if response is empty or unexpected format)
      console.log('🔵 [GosiService.addOrUpdateGosi] Using default success response')
      return {
        success: true,
        message: 'GOSI settings saved successfully',
        data: data,
      } as ApiResponse<GosiVm>
    } catch (error: any) {
      console.error('❌ [GosiService.addOrUpdateGosi] Error:', error)
      console.error('❌ [GosiService.addOrUpdateGosi] Error response:', error.response)
      console.error('❌ [GosiService.addOrUpdateGosi] Error response data:', error.response?.data)
      console.error('❌ [GosiService.addOrUpdateGosi] Error status:', error.response?.status)
      console.error('❌ [GosiService.addOrUpdateGosi] Error config:', error.config)
      
      // Extract error message from response
      const errorMessage = error.response?.data?.msg || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to save GOSI settings'
      
      throw new Error(errorMessage)
    }
  }
}

export const gosiService = new GosiService()
